import React, { useState, useEffect, useContext, useMemo } from "react";
import { Form, Button, Modal } from "react-bootstrap";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Check, ChevronDown } from "lucide-react";
import GlobalContext from "../../context/GlobalContext";
import SelectCompanyType from "../../components/select/SelectCompanyType";
import SelectCountry from "../../components/select/SelectCountry";
import TimeZoneSelect from "../../components/TimeZoneSelect";
import InputWorkingDaysTime from "../../components/InputWorkingDaysTime";
import { registerCompany, fetchRegisterSuggestions } from "../../api/CompanyApi";
import { userProfile } from "../../api/userProfileApi";
import axiosInstance from "../../services/axiosInstance";
import AuthLoginForm from "../../components/auth/AuthLoginForm";
import AuthRegisterForm from "../../components/auth/AuthRegisterForm";
import { Helmet } from "react-helmet-async";
import sewoorkLogo from "../../assets/images/sewoork-logo.png";
import "./RegisterCompany.css";
import swal from "../../services/swalConfig";
import i18n, { tr } from "../../i18n";

/*
 * /get-started — pendaftaran organisasi ringkas.
 *
 * Hanya 3 input wajib: nama, negara, jenis organisasi. Zona waktu, mata uang, jadwal kerja,
 * cuti tahunan & skema payroll diturunkan dari negara oleh API (sama dengan ringkasan yang
 * tampil di sini) dan bisa disesuaikan lewat "Sesuaikan". Data lain (alamat, kontak, logo,
 * lokasi absensi, undangan tim) dilengkapi lewat checklist "Progres pengaturan" di dashboard.
 */

const PLANS = ["free", "startup", "enterprise"];

// Harga cadangan bila /landing/pricing tidak bisa diambil (USD, sama dengan landing page).
const FALLBACK_PRICES = { free: { USD: 0 }, startup: { USD: 1.25 }, enterprise: { USD: 2.2 } };

// Mata uang harga per bahasa — sama dengan `pricing.currency` di landing page.
const CURRENCY_BY_LANGUAGE = { id: "IDR" };

const DAY_INDEX = {
  Monday: 0,
  Tuesday: 1,
  Wednesday: 2,
  Thursday: 3,
  Friday: 4,
  Saturday: 5,
  Sunday: 6,
};

const DO_LATER = "do_later";

const feature = (labelKey, extra = {}) => ({
  get label() {
    return tr(`register:${labelKey}`);
  },
  get value() {
    return extra.valueKey ? tr(`register:${extra.valueKey}`) : undefined;
  },
  available: extra.available,
});

const planFeatures = {
  free: [
    feature("feature_gps", { available: true }),
    feature("feature_qr_checkin", { available: true }),
    feature("feature_leave", { available: true }),
    feature("feature_payroll", { available: true }),
    feature("feature_geofence", { valueKey: "feature_geofence_free" }),
  ],
  startup: [
    feature("feature_gps", { available: true }),
    feature("feature_qr_checkin", { available: true }),
    feature("feature_qr_id_cards", { available: true }),
    feature("feature_payroll", { available: true }),
    feature("feature_geofence", { valueKey: "feature_geofence_startup" }),
  ],
  enterprise: [
    feature("feature_gps", { available: true }),
    feature("feature_qr_id_cards", { available: true }),
    feature("feature_multi_company", { available: true }),
    feature("feature_payroll", { available: true }),
    feature("feature_geofence", { valueKey: "feature_geofence_enterprise" }),
  ],
};

const formatPrice = (amount, currency) => {
  if (currency === "IDR") {
    return `Rp${Number(amount).toLocaleString("id-ID", { maximumFractionDigits: 0 })}`;
  }
  const hasCents = Math.floor(amount) !== Number(amount);
  return `$${Number(amount).toFixed(hasCents ? 2 : 0)}`;
};

// Nomor HP: boleh +, spasi, titik, tanda hubung & kurung; 8–15 digit (batas E.164).
const isValidPhone = (value = "") => {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, "");
  return /^\+?[\d\s().-]+$/.test(trimmed) && digits.length >= 8 && digits.length <= 15;
};

const shortTime = (value) => String(value || "").slice(0, 5);

/**
 * Ringkasan jadwal kerja, mis. "Sen–Jum 08:00–17:00" (nama hari mengikuti bahasa aktif).
 * Hari berurutan dengan jam yang sama digabung.
 */
const summarizeSchedule = (items = [], language = "en") => {
  const weekday = new Intl.DateTimeFormat(language, { weekday: "short" });
  // 1 Januari 2024 adalah hari Senin.
  const dayName = (index) => weekday.format(new Date(2024, 0, 1 + index));

  const days = items
    .filter((item) => item.is_checked && DAY_INDEX[item.day] !== undefined)
    .map((item) => ({
      index: DAY_INDEX[item.day],
      time: `${shortTime(item.time_in)}–${shortTime(item.time_out)}`,
    }))
    .sort((a, b) => a.index - b.index);

  const groups = [];
  days.forEach((day) => {
    const last = groups[groups.length - 1];
    if (last && last.time === day.time && last.to === day.index - 1) {
      last.to = day.index;
    } else {
      groups.push({ from: day.index, to: day.index, time: day.time });
    }
  });

  return groups
    .map((group) => {
      const range =
        group.from === group.to
          ? dayName(group.from)
          : `${dayName(group.from)}–${dayName(group.to)}`;
      return `${range} ${group.time}`;
    })
    .join(", ");
};

const buildScheduleDefaultValue = (scheduleItems = []) => ({
  schedules: scheduleItems.map((item, index) => ({
    schedule_id: `suggestion-${index + 1}`,
    schedule_day: item.day,
    schedule_time_in: item.time_in,
    schedule_time_out: item.time_out,
    map_id: item.coordinate || null,
    is_checked: item.is_checked === true,
  })),
});

const RegisterCompany = () => {
  const [selectedPlan, setSelectedPlan] = useState("free");
  const [prices, setPrices] = useState(FALLBACK_PRICES);
  const [preset, setPreset] = useState(null);
  const [presetLoading, setPresetLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [authNotice, setAuthNotice] = useState("");
  const [pendingSubmitAfterAuth, setPendingSubmitAfterAuth] = useState(false);
  const { setLoading, isLoading, user, setUser } = useContext(GlobalContext);
  const navigate = useNavigate();

  const methods = useForm({
    defaultValues: {
      name: "",
      phone: "",
      plan: "free",
      payroll_suggestion_key: "",
      default_paid_leave: 0,
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const countryId = watch("country_id");
  const payrollKey = watch("payroll_suggestion_key");
  const language = i18n.language || "en";
  const priceCurrency = CURRENCY_BY_LANGUAGE[language] || "USD";

  // Paket dari URL (?plan=startup) — dikirim dari landing page sewoork.com.
  useEffect(() => {
    const planParam = new URLSearchParams(window.location.search).get("plan");
    if (planParam && PLANS.includes(planParam.toLowerCase())) {
      setSelectedPlan(planParam.toLowerCase());
    }
  }, []);

  useEffect(() => {
    setValue("plan", selectedPlan);
  }, [selectedPlan, setValue]);

  // Harga paket dari API yang sama dengan landing page (tabel landing_pricing).
  useEffect(() => {
    let active = true;
    axiosInstance
      .get("../landing/pricing")
      .then((response) => {
        const plans = response?.data?.data?.plans || [];
        if (!active || !plans.length) return;
        setPrices(
          plans.reduce((acc, plan) => ({ ...acc, [plan.key]: plan.prices || {} }), {}),
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Ringkasan pengaturan otomatis untuk negara yang dipilih (aturan sama dengan API register).
  useEffect(() => {
    if (!countryId) {
      setPreset(null);
      return;
    }

    let active = true;
    setPresetLoading(true);
    fetchRegisterSuggestions({ countryId })
      .then((response) => {
        if (!active) return;
        const country = response?.data?.countries?.[0] || null;
        setPreset(country);
        setValue("default_paid_leave", country?.default_paid_leave || 0);
        setValue(
          "payroll_suggestion_key",
          country?.payroll_options?.[0]?.key || DO_LATER,
        );
        if (country?.time_zone) {
          setValue("timeZone", country.time_zone);
        }
      })
      .catch(() => active && setPreset(null))
      .finally(() => active && setPresetLoading(false));

    return () => {
      active = false;
    };
  }, [countryId, setValue]);

  const scheduleDefault = useMemo(
    () => (preset ? buildScheduleDefaultValue(preset.schedule?.items || []) : null),
    [preset],
  );

  const timeZoneDefault = useMemo(
    () => (preset?.time_zone ? { id: preset.time_zone, text: preset.time_zone } : null),
    [preset],
  );

  const payrollOptions = preset?.payroll_options || [];
  const selectedPayroll = payrollOptions.find((option) => option.key === payrollKey);
  const scheduleSummary = summarizeSchedule(preset?.schedule?.items, language);
  const paidLeave = Number(watch("default_paid_leave") ?? preset?.default_paid_leave ?? 0);

  const buildPayload = (data) => {
    const payload = {
      name: data.name.trim(),
      company_type: data.company_type,
      country_id: data.country_id,
      phone: data.phone.trim(),
      plan: selectedPlan,
    };

    // Tanpa "Sesuaikan", API menurunkan semuanya dari negara.
    if (showAdvanced) {
      payload.timeZone = data.timeZone;
      payload.default_paid_leave = data.default_paid_leave;
      payload.schedule = data.schedule;
      payload.payroll_suggestion_key = data.payroll_suggestion_key || DO_LATER;
    }

    return payload;
  };

  const onSubmit = async (data) => {
    const payload = buildPayload(data);

    if (!user) {
      localStorage.setItem("pendingRegisterCompany", JSON.stringify(payload));
      setAuthNotice(
        tr("register:continue_registration_for_name_by_logging", { name: payload.name }),
      );
      setAuthTab("login");
      setShowAuthModal(true);
      return;
    }

    setLoading(true);
    try {
      const response = await registerCompany(payload);

      if (response?.status === 200) {
        const latestProfile = await userProfile();

        if (latestProfile?.data) {
          setUser(latestProfile.data);
          localStorage.setItem("userProfile", JSON.stringify(latestProfile.data));
        }

        navigate("/dashboard");
        return;
      }

      // Sesi habis / JWT tidak valid: simpan data lalu minta login ulang;
      // pendaftaran dilanjutkan otomatis setelah login berhasil.
      if (!response || response?.status === 401) {
        localStorage.removeItem("userProfile");
        setUser(null);
        localStorage.setItem("pendingRegisterCompany", JSON.stringify(payload));
        setAuthNotice(
          tr("register:your_session_has_expired_please_log", { name: payload.name }),
        );
        setAuthTab("login");
        setShowAuthModal(true);
        return;
      }

      if (response?.errors) {
        Object.entries(response.errors).forEach(([field, message]) => {
          methods.setError(field, { type: "server", message });
        });
      }

      swal.fire({
        title: tr("register:gs_failed_title"),
        text: response?.message || tr("register:something_went_wrong_please_try_again"),
        icon: "error",
        confirmButtonColor: "#0d6ef0",
      });
    } catch (error) {
      console.error("Registration failed", error);
      swal.fire({
        title: tr("register:gs_failed_title"),
        text: tr("register:an_unexpected_error_occurred_please_try"),
        icon: "error",
        confirmButtonColor: "#0d6ef0",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pendingSubmitAfterAuth && user && !showAuthModal) {
      setPendingSubmitAfterAuth(false);
      handleSubmit(onSubmit)();
    }
  }, [pendingSubmitAfterAuth, user, showAuthModal]);

  const planPrice = (plan) => {
    const planPrices = prices[plan] || {};
    const currency = planPrices[priceCurrency] !== undefined ? priceCurrency : "USD";
    const amount = planPrices[currency] ?? FALLBACK_PRICES[plan].USD;
    return formatPrice(amount, currency);
  };

  return (
    <>
      <Helmet>
        <title>{tr("register:register_company")}</title>
      </Helmet>

      <div className="gs-page">
        <div className="gs-shell">
          {/* Panel kiri: paket & apa yang bisa dilengkapi nanti */}
          <aside className="gs-aside">
            <img
              className="gs-aside__logo"
              src={sewoorkLogo}
              alt="Sewoork"
              width="120"
              height="31"
            />
            <h2 className="gs-aside__title">{tr("register:gs_brand_title")}</h2>

            <div className="gs-plans" role="radiogroup" aria-label={tr("register:gs_plan_heading")}>
              <div className="gs-label gs-label--small">{tr("register:gs_plan_heading")}</div>
              {PLANS.map((plan) => {
                const active = plan === selectedPlan;
                return (
                  <button
                    key={plan}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`gs-plan ${active ? "is-active" : ""}`}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    <span className="gs-plan__radio" aria-hidden="true">
                      {active && <Check size={12} strokeWidth={3.5} />}
                    </span>
                    <span className="gs-plan__name">{tr(`register:gs_plan_${plan}`)}</span>
                    <span className="gs-plan__price">
                      {planPrice(plan)}
                      <small>{tr("register:gs_per_user_month")}</small>
                    </span>
                  </button>
                );
              })}
              <ul className="gs-plan-features">
                {planFeatures[selectedPlan].map((item) => (
                  <li key={item.label}>
                    <Check size={14} strokeWidth={2.5} />
                    <span>
                      {item.label}
                      {item.value ? <em> ({item.value})</em> : null}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="gs-aside__note">{tr("register:gs_change_plan_later")}</p>
            </div>

            <div className="gs-later">
              <strong>{tr("register:gs_later_title")}</strong>
              <p>{tr("register:gs_later_text")}</p>
            </div>
          </aside>

          {/* Form */}
          {/* div, bukan <main>: theme.css memberi <main> latar & padding layout dashboard. */}
          <div className="gs-main" role="main">
            <img className="gs-main__logo" src={sewoorkLogo} alt="Sewoork" width="110" height="28" />
            <h1 className="gs-title">{tr("register:gs_title")}</h1>
            <p className="gs-subtitle">{tr("register:gs_subtitle")}</p>

            <FormProvider {...methods}>
              <Form onSubmit={handleSubmit(onSubmit)} noValidate className="gs-form">
                <Form.Group className="gs-field">
                  <Form.Label htmlFor="gs-name">{tr("register:gs_org_name")}</Form.Label>
                  <Form.Control
                    id="gs-name"
                    size="lg"
                    autoComplete="organization"
                    autoFocus
                    {...methods.register("name", {
                      required: tr("register:company_name_is_required"),
                      validate: (value) =>
                        value.trim() !== "" || tr("register:company_name_is_required"),
                    })}
                    isInvalid={!!errors.name}
                    placeholder={tr("register:gs_org_name_placeholder")}
                  />
                  <Form.Control.Feedback type="invalid">{errors.name?.message}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="gs-field">
                  <Form.Label htmlFor="gs-phone">{tr("register:gs_phone")}</Form.Label>
                  <Form.Control
                    id="gs-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    {...methods.register("phone", {
                      required: tr("register:gs_phone_required"),
                      validate: (value) =>
                        isValidPhone(value) || tr("register:gs_phone_invalid"),
                    })}
                    isInvalid={!!errors.phone}
                    placeholder={tr("register:gs_phone_placeholder")}
                  />
                  <Form.Control.Feedback type="invalid">{errors.phone?.message}</Form.Control.Feedback>
                </Form.Group>

                <div className="gs-row">
                  <Form.Group className="gs-field">
                    <Form.Label>{tr("register:gs_country")}</Form.Label>
                    <SelectCountry
                      name="country_id"
                      rules={{ required: tr("register:country_is_required") }}
                    />
                    {errors.country_id && (
                      <div className="invalid-feedback d-block">
                        {errors.country_id.message || tr("register:country_is_required")}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="gs-field">
                    <Form.Label>{tr("register:gs_org_type")}</Form.Label>
                    <SelectCompanyType
                      name="company_type"
                      rules={{ required: tr("register:organization_type_is_required") }}
                    />
                    {errors.company_type && (
                      <div className="invalid-feedback d-block">
                        {errors.company_type.message || tr("register:organization_type_is_required")}
                      </div>
                    )}
                  </Form.Group>
                </div>

                {/* Ringkasan pengaturan otomatis dari negara */}
                <section className={`gs-auto ${preset ? "is-ready" : ""}`} aria-live="polite">
                  {!countryId ? (
                    <p className="gs-auto__empty">{tr("register:gs_select_country_first")}</p>
                  ) : presetLoading && !preset ? (
                    <p className="gs-auto__empty">{tr("register:gs_loading_setup")}</p>
                  ) : preset ? (
                    <>
                      <div className="gs-auto__head">
                        <div>
                          <div className="gs-auto__title">
                            {tr("register:gs_auto_title", { country: preset.name })}
                          </div>
                          <div className="gs-auto__hint">{tr("register:gs_auto_hint")}</div>
                        </div>
                        <button
                          type="button"
                          className="gs-link"
                          aria-expanded={showAdvanced}
                          aria-controls="gs-advanced"
                          onClick={() => setShowAdvanced((open) => !open)}
                        >
                          {showAdvanced ? tr("register:gs_hide") : tr("register:gs_customize")}
                          <ChevronDown size={16} className={showAdvanced ? "is-flipped" : ""} />
                        </button>
                      </div>

                      <dl className="gs-auto__list">
                        <dt>{tr("register:gs_currency")}</dt>
                        <dd>
                          {preset.currency || "-"}
                          {preset.currency_symbol ? ` (${preset.currency_symbol})` : ""}
                        </dd>
                        <dt>{tr("register:gs_time_zone")}</dt>
                        <dd>{preset.time_zone || "-"}</dd>
                        <dt>{tr("register:gs_work_schedule")}</dt>
                        <dd>{scheduleSummary || "-"}</dd>
                        <dt>{tr("register:gs_paid_leave")}</dt>
                        <dd>{tr("register:gs_paid_leave_value", { count: paidLeave })}</dd>
                        <dt>{tr("register:gs_payroll")}</dt>
                        <dd>{selectedPayroll ? selectedPayroll.label : tr("register:gs_payroll_later")}</dd>
                      </dl>

                      {showAdvanced && (
                        <div className="gs-advanced" id="gs-advanced">
                          <div className="gs-row">
                            <Form.Group className="gs-field">
                              <Form.Label>{tr("shared:time_zone")}</Form.Label>
                              <TimeZoneSelect
                                name="timeZone"
                                defaultValue={timeZoneDefault}
                                rules={{ required: tr("shared:time_zone_is_required") }}
                              />
                            </Form.Group>
                            <Form.Group className="gs-field">
                              <Form.Label htmlFor="gs-leave">{tr("register:default_paid_leave_days")}</Form.Label>
                              <Form.Control
                                id="gs-leave"
                                type="number"
                                min={0}
                                {...methods.register("default_paid_leave", {
                                  min: { value: 0, message: tr("register:paid_leave_cannot_be_negative") },
                                  pattern: { value: /^\d+$/, message: tr("register:please_enter_a_valid_number") },
                                })}
                                isInvalid={!!errors.default_paid_leave}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.default_paid_leave?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>

                          <div className="gs-field">
                            <div className="gs-label">{tr("register:gs_payroll")}</div>
                            <div className="gs-choices" role="radiogroup">
                              {[...payrollOptions, { key: DO_LATER, label: tr("register:gs_payroll_later"), description: tr("register:gs_payroll_later_desc") }].map((option) => (
                                <button
                                  key={option.key}
                                  type="button"
                                  role="radio"
                                  aria-checked={payrollKey === option.key}
                                  className={`gs-choice ${payrollKey === option.key ? "is-active" : ""}`}
                                  onClick={() => setValue("payroll_suggestion_key", option.key)}
                                >
                                  <strong>{option.label}</strong>
                                  <span>{option.description}</span>
                                </button>
                              ))}
                            </div>
                            {!payrollOptions.length && (
                              <small className="text-muted">{tr("register:gs_no_payroll_preset")}</small>
                            )}
                          </div>

                          <div className="gs-field">
                            <div className="gs-label">{tr("register:gs_work_schedule")}</div>
                            <div className="gs-schedule">
                              <InputWorkingDaysTime showLocation={false} defaultValue={scheduleDefault} />
                            </div>
                            {errors.schedule && (
                              <div className="invalid-feedback d-block">
                                {tr("register:gs_schedule_required")}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  ) : null}
                </section>

                <Button type="submit" size="lg" className="gs-submit" disabled={isLoading}>
                  {isLoading ? tr("register:registering") : tr("register:gs_submit")}
                </Button>
                <p className="gs-footnote">{tr("register:gs_footnote")}</p>
              </Form>
            </FormProvider>
          </div>
        </div>
      </div>

      <Modal
        show={showAuthModal}
        onHide={() => setShowAuthModal(false)}
        centered
        size="md"
        className="register-auth-modal"
      >
        <Modal.Header closeButton>
          {/* Tanpa tab: tiap form punya link "Belum punya akun? / Sudah punya akun?" untuk berpindah. */}
          <Modal.Title>
            {authTab === "login" ? tr("register:gs_auth_login_title") : tr("register:gs_auth_register_title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {authTab === "login" ? (
            <AuthLoginForm
              registrationMessage={authNotice}
              onDismissMessage={() => setAuthNotice("")}
              onRequireRegister={() => setAuthTab("register")}
              onSuccess={() => {
                setPendingSubmitAfterAuth(true);
                setShowAuthModal(false);
              }}
              showGoogle
            />
          ) : (
            <AuthRegisterForm
              onSuccess={(response) => {
                setAuthNotice(
                  response?.message || tr("register:registration_successful_please_check_your_email"),
                );
                setAuthTab("login");
              }}
              onRequireLogin={() => setAuthTab("login")}
              showGoogle
              registerSource="console"
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default RegisterCompany;
